import React, { useState, useEffect } from "react";
import {
  Modal, ModalHeader, ModalBody, Button, Form, FormGroup,
  Label, Input, Card, CardBody, CardHeader, Row, Col, FormFeedback,
  Spinner, Alert, FormText, Collapse
} from "reactstrap";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { Formik, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { getMasterSheetLayout } from "./Helpers/api";
import { Leases } from "./Helpers/Leases";
import { Addendums } from "./Helpers/Addendum/Addendums";
import { Documents } from "./Helpers/AdditionalDocuments/Documents";
import { Memos } from "./Helpers/AdditionalDocuments/Memos";
import { MetaFields } from "./Helpers/Meta/MetaFields";
import { Pictures } from "./Helpers/AdditionalDocuments/Components/Pictures";
import { DOCUMENT_TYPES } from './Helpers/AdditionalDocuments/Constants';
import Swal from "sweetalert2";

const NON_EDITABLE_FIELDS = {
  unitNo: 'not-editable',
  sqFt: 'not-editable',
  style: 'not-editable',
  layout: 'not-editable',
  targetRent: 'editable-by-company-user'
};

const FORCE_REQUIRED_FIELDS = ['insuranceExpiry'];

const defaultStatusOptions = [
  { value: 'Start Pad', label: 'Start Pad' },
  { value: 'Change Rent', label: 'Change Rent' },
  { value: 'Do Not Renew', label: 'Do Not Renew' },
  { value: 'Not Renewing', label: 'Not Renewing' },
  { value: 'Lease Renewal Sent', label: 'Lease Renewal Sent' },
];

const autoConvertValue = (value, expectedType) => {
  if ((expectedType === 'text' || expectedType === 'email' || expectedType === 'string') &&
    typeof value === 'number' && !isNaN(value)) {
    return String(value);
  }
  return value;
};

const isNAValue = (value) =>
  typeof value === 'string' && value.trim().toUpperCase() === 'N/A';

const buildAccountSchema = (label, isIncludedInRent) => {
  if (isIncludedInRent === true) {
    return Yup.string().nullable();
  }

  let schema = Yup.string()
    .required(`${label} provider/account number is required`)
    .test('is-not-empty', `${label} provider/account number is required`,
      value => value !== '' && value !== null && value !== undefined);

  if (isIncludedInRent === false) {
    schema = schema.test(
      'no-na-when-not-included',
      `${label} is not included in rent — a valid account number is required (N/A is not allowed)`,
      value => !isNAValue(value)
    );
  }

  return schema;
};

const generateValidationSchema = (formLayout, utilitiesAvailable = null) => {
  const schemaObj = {};

  const buildFieldSchema = (field) => {
    let schema;

    switch (field.type) {
      case 'text':
      case 'email':
        schema = Yup.string().transform((value, originalValue) => {
          if (typeof originalValue === 'number' && !isNaN(originalValue)) {
            return String(originalValue);
          }
          return value;
        });
        if (field.type === 'email') schema = schema.email('Invalid email address');
        if (field.minLength) schema = schema.min(field.minLength, `Must be at least ${field.minLength} characters`);
        if (field.maxLength) schema = schema.max(field.maxLength, `Must be at most ${field.maxLength} characters`);
        if (field.pattern) schema = schema.matches(new RegExp(field.pattern), 'Invalid format');
        if (field.enum) schema = schema.oneOf(field.enum, 'Invalid value');
        break;

      case 'number':
        schema = Yup.number().transform((value, originalValue) => {
          if (typeof originalValue === 'string' && originalValue.trim() !== '') {
            const parsed = parseFloat(originalValue.replace(/[^\d.]/g, ''));
            return isNaN(parsed) ? originalValue : parsed;
          }
          return value;
        }).typeError('Must be a number');
        if (field.min !== undefined) schema = schema.min(field.min, `Must be at least ${field.min}`);
        if (field.max !== undefined) schema = schema.max(field.max, `Must be at most ${field.max}`);
        break;

      case 'boolean':
        schema = Yup.boolean();
        break;

      case 'date':
        schema = Yup.date().nullable().typeError('Invalid date');
        if (field.min) schema = schema.min(field.min, `Date must be after ${field.min}`);
        if (field.max) schema = schema.max(field.max, `Date must be before ${field.max}`);
        break;

      case 'select':
        schema = Yup.mixed().transform((value, originalValue) => {
          if (typeof originalValue === 'number' && !isNaN(originalValue)) {
            return { value: String(originalValue), label: String(originalValue) };
          }
          return value;
        });
        if (field.enum) {
          schema = schema.test('is-valid-option', 'Please select a valid option', value => {
            if (!value) return !field.required && !FORCE_REQUIRED_FIELDS.includes(field.key);
            const allowedValues = field.enum.map(v => String(v));
            if (typeof value === 'object' && value !== null) {
              return allowedValues.includes(String(value.value));
            }
            return allowedValues.includes(String(value));
          });
        } else if (field.options) {
          schema = schema.test('is-valid-type', '', value => {
            if (!value) return !field.required && !FORCE_REQUIRED_FIELDS.includes(field.key);
            return true;
          });
        }
        break;

      case 'array':
        schema = Yup.array().of(
          Yup.object().shape(
            field.fields.reduce((acc, subField) => {
              acc[subField.key] = buildFieldSchema(subField);
              return acc;
            }, {})
          )
        );
        if (field.minItems) schema = schema.min(field.minItems, `Must have at least ${field.minItems} items`);
        if (field.maxItems) schema = schema.max(field.maxItems, `Must have at most ${field.maxItems} items`);
        break;

      case 'group':
        schema = Yup.object().shape(
          field.fields.reduce((acc, subField) => {
            acc[subField.key] = buildFieldSchema(subField);
            return acc;
          }, {})
        );
        break;

      default:
        schema = Yup.string().transform((value, originalValue) => {
          if (typeof originalValue === 'number' && !isNaN(originalValue)) {
            return String(originalValue);
          }
          return value;
        });
    }

    if (field.key in NON_EDITABLE_FIELDS) {
      return schema.nullable();
    }

    const isRequired = field.required || FORCE_REQUIRED_FIELDS.includes(field.key);
    return isRequired ? schema.required(`${field.label || field.key} is required`) : schema.nullable();
  };

  formLayout.forEach(field => {
    schemaObj[field.key] = buildFieldSchema(field);
  });

  schemaObj['meta'] = Yup.object().shape({
    accounts: Yup.object().shape({
      heat: buildAccountSchema('Heat', utilitiesAvailable ? utilitiesAvailable.heat === true : null),
      water: buildAccountSchema('Water', utilitiesAvailable ? utilitiesAvailable.water === true : null),
      electricity: buildAccountSchema('Electricity', utilitiesAvailable ? utilitiesAvailable.electricity === true : null)
    }).required('Accounts information is required'),
    parking: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string()
            .required('Parking type is required')
            .test('is-not-empty', 'Parking type is required', value => value !== ''),
          stall_no: Yup.string()
            .required('Parking stall number is required')
            .test('is-not-empty', 'Parking stall number is required', value => value !== '')
        })
      )
      .min(1, 'At least one parking entry is required')
      .max(5, 'Maximum 5 parking entries allowed')
      .required('At least one parking entry is required'),
    storage: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string()
            .required('Storage type is required')
            .test('is-not-empty', 'Storage type is required', value => value !== ''),
          stall_no: Yup.string()
            .required('Storage stall number is required')
            .test('is-not-empty', 'Storage stall number is required', value => value !== '')
        })
      )
      .min(1, 'At least one storage entry is required')
      .max(5, 'Maximum 5 storage entries allowed')
      .required('At least one storage entry is required'),
    fobs: Yup.array()
      .of(
        Yup.object().shape({
          stall_no: Yup.string()
            .required('FOB number is required')
            .test('is-not-empty', 'FOB number is required', value => value !== ''),
          type: Yup.string()
            .required('FOB type is required')
            .test('is-not-empty', 'FOB type is required', value => value !== ''),
          name: Yup.string()
            .required('FOB name is required')
            .test('is-not-empty', 'FOB name is required', value => value !== '')
        })
      )
      .min(1, 'At least one FOB entry is required')
      .max(7, 'Maximum 7 FOBs allowed')
      .required('At least one FOB entry is required'),
    keys: Yup.array()
      .of(
        Yup.object().shape({
          main_entry: Yup.string()
            .required('Main entry key is required')
            .test('is-not-empty', 'Main entry key is required', value => value !== ''),
          suite: Yup.string()
            .required('Suite key is required')
            .test('is-not-empty', 'Suite key is required', value => value !== ''),
          mailbox: Yup.string()
            .required('Mailbox key is required')
            .test('is-not-empty', 'Mailbox key is required', value => value !== ''),
          storage: Yup.string()
            .required('Storage key is required')
            .test('is-not-empty', 'Storage key is required', value => value !== '')
        })
      )
      .min(1, 'At least one key set is required')
      .max(5, 'Maximum 5 key sets allowed')
      .required('At least one key set is required'),
    occupants: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string()
            .required('Occupant name is required')
            .test('is-not-empty', 'Occupant name is required', value => value !== ''),
          phone: Yup.string()
            .required('Occupant phone is required')
            .test('is-not-empty', 'Occupant phone is required', value => value !== ''),
          email: Yup.string()
            .test(
              'is-valid-email-or-na',
              'Invalid email address',
              value => value === 'N/A' || Yup.string().email().isValidSync(value)
            )
            .required('Occupant email is required'),
          age: Yup.number()
            .nullable()
            .transform((value, originalValue) => {
              if (originalValue === '' || originalValue === 'N/A') return null;
              return value;
            })
            .typeError('Age must be a number or N/A')
        })
      )
      .min(1, 'At least one occupant is required')
      .max(10, 'Maximum 10 occupants allowed')
      .required('At least one occupant is required')
  }).required('Meta information is required');

  return Yup.object().shape(schemaObj);
};

const AccordionContext = React.createContext({
  openGroups: {},
  toggleGroup: () => { }
});

const safeFormatDate = (date) => {
  if (!date) return "";

  try {
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime()) ? dateObj.toISOString().split('T')[0] : "";
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

const getDisplayValue = (value, field) => {
  if (value === null || value === undefined) return '';
  if (field.type === 'number' && typeof value === 'number') {
    return `$${value.toFixed(2)}`;
  }
  return value;
};

const getNestedValue = (obj, path) => {
  if (!obj) return undefined;
  const keys = path.split('.');
  return keys.reduce((o, key) => {
    if (o && typeof o === 'object') {
      if (key.includes('[') && key.includes(']')) {
        const arrayKey = key.split('[')[0];
        const index = parseInt(key.split('[')[1].split(']')[0]);
        return o[arrayKey] ? o[arrayKey][index] : undefined;
      }
      return o[key];
    }
    return undefined;
  }, obj);
};

function getNestedError(errors, path) {
  if (!errors) return null;
  return getNestedValue(errors, path);
}

function getNestedTouch(touched, path) {
  if (!touched) return null;
  return getNestedValue(touched, path);
}

const hasBasicFieldErrors = (errors, touched, formLayout) => {
  const hasErrors = formLayout
    .filter(field => field.type !== "array" && field.type !== "group")
    .some(field => {
      const hasError = getNestedError(errors, field.key);
      const isTouched = getNestedTouch(touched, field.key);
      return hasError && isTouched;
    });

  return !hasErrors;
};

const DynamicField = ({ field, name, values, setFieldValue, errors, touched, isNested = false, isCompanyUser }) => {
  const hasError = getNestedError(errors, name) && getNestedTouch(touched, name);
  const errorMessage = hasError ? getNestedError(errors, name) : "";
  const { openGroups, toggleGroup } = React.useContext(AccordionContext);
  const [isEditingNumber, setIsEditingNumber] = useState(false);
  const [isEditingFee, setIsEditingFee] = useState(false);

  const isFeeField = (fieldKey) => {
    return fieldKey && fieldKey.toLowerCase().includes('fee');
  };

  const formatFeeValue = (value, isEditing = false) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    if (isEditing) {
      const num = parseFloat(value);
      return isNaN(num) ? '' : num.toString();
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return '';
    }
    return `$${num.toFixed(2)}`;
  };

  const handleFeeFieldChange = (e, fieldName) => {
    const rawValue = e.target.value.replace(/[^\d.]/g, '');
    const decimalCount = (rawValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }
    setFieldValue(fieldName, rawValue);
  };

  const handleFeeFieldBlur = (e, fieldName) => {
    setIsEditingFee(false);
    const cleanValue = e.target.value.replace(/[^\d.]/g, '');
    if (cleanValue === '') {
      setFieldValue(fieldName, '');
    } else {
      const numValue = parseFloat(cleanValue);
      if (!isNaN(numValue)) {
        setFieldValue(fieldName, numValue);
      } else {
        setFieldValue(fieldName, '');
      }
    }
  };

  const enhancedSetFieldValue = (fieldName, value) => {
    let convertedValue = value;
    if (isFeeField(field.key)) {
      convertedValue = value;
    } else {
      convertedValue = autoConvertValue(value, field.type);
    }
    setFieldValue(fieldName, convertedValue);
  };

  const isFieldEditable = () => {
    if (!(field.key in NON_EDITABLE_FIELDS)) return true;
    if (NON_EDITABLE_FIELDS[field.key] === 'editable-by-company-user') {
      return isCompanyUser;
    }
    return false;
  };

  const isFieldDisabled = !isFieldEditable();

  const isRequired = field.required || FORCE_REQUIRED_FIELDS.includes(field.key);

  const calculateResidentCount = () => {
    const fieldValue = getNestedValue(values, name);
    const occupantsCount = values.meta?.occupants?.length || 0;
    let tenantCount = 0;

    if (field.type === 'array') {
      tenantCount = Array.isArray(fieldValue) ? fieldValue.length : 0;
      return {
        count: tenantCount + occupantsCount,
        explanation: `Calculated as: ${tenantCount} tenant(s) from the array + ${occupantsCount} occupant(s) from meta.occupants`
      };
    } else if (field.type === 'text') {
      if (typeof fieldValue === 'string' && fieldValue.trim()) {
        const separators = /,|\band\b|&/;
        const tenants = fieldValue
          .split(separators)
          .map(name => name.trim())
          .filter(name => name.length > 0);
        tenantCount = tenants.length;
      }
      return {
        count: tenantCount + occupantsCount,
        explanation: `${tenantCount} tenant(s) (split by commas, 'and', or '&') + ${occupantsCount} occupant(s)`
      };
    }

    return {
      count: occupantsCount,
      explanation: `0 tenant(s) (no valid tenant data) + ${occupantsCount} occupant(s)`
    };
  };

  const isTenantField = field.key === 'tenants' || field.key === 'tenant';
  const residentInfo = isTenantField ? calculateResidentCount() : null;

  if (field.key === 'status') {
    const currentValue = getNestedValue(values, name);
    const selectedOption = currentValue
      ? typeof currentValue === 'object' && currentValue.value
        ? { ...currentValue, value: String(currentValue.value), label: String(currentValue.label) }
        : { value: String(currentValue), label: String(currentValue) }
      : null;

    return (
      <FormGroup className={isNested ? "mb-2" : "mb-3"}>
        <Label for={name}>
          {field.label}{isRequired && <span className="text-danger">*</span>}
        </Label>
        <CreatableSelect
          id={name}
          name={name}
          value={selectedOption}
          options={defaultStatusOptions}
          onChange={(option) => {
            if (!isFieldDisabled) {
              const convertedOption = option ? String(option.value) : null;
              setFieldValue(name, convertedOption);
            }
          }}
          onCreateOption={(inputValue) => {
            if (!isFieldDisabled) {
              const newOption = { value: String(inputValue), label: String(inputValue) };
              setFieldValue(name, newOption);
            }
          }}
          placeholder={`Select or type ${field.label}`}
          isClearable={!isRequired}
          isDisabled={isFieldDisabled}
          className={hasError ? 'is-invalid' : ''}
          formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
          styles={{
            control: (base) => ({
              ...base,
              borderColor: hasError ? '#dc3545' : base.borderColor,
              '&:hover': {
                borderColor: hasError ? '#dc3545' : base.borderColor,
              },
            }),
          }}
        />
        {hasError && <div className="invalid-feedback d-block">{errorMessage}</div>}
        {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
        {field.description && !isFieldDisabled && <FormText>{field.description}</FormText>}
      </FormGroup>
    );
  }

  switch (field.type) {
    case "text":
    case "email":
      const currentValue = getNestedValue(values, name);
      return (
        <FormGroup className={isNested ? "mb-2" : "mb-3"}>
          <Label for={name}>
            {field.label}{isRequired && <span className="text-danger">*</span>}
            {isTenantField && (
              <>
                <span className="ms-2 text-muted">
                  (Residents: {residentInfo.count})
                </span>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip
                      id={`tooltip-${name.replace(/\./g, '-')}`}
                      style={{ zIndex: 1050 }}
                    >
                      {residentInfo.explanation}
                    </Tooltip>
                  }
                >
                  <span className="ms-1" style={{ cursor: 'pointer' }}>
                    <FeatherIcon icon="info" size={16} className="text-muted" />
                  </span>
                </OverlayTrigger>
              </>
            )}
          </Label>
          <Field
            name={name}
            id={name}
            type={field.type}
            as={Input}
            placeholder={field.example || ""}
            value={isFeeField(field.key) ? formatFeeValue(currentValue, isEditingFee) : currentValue || ''}
            invalid={!!hasError}
            disabled={isFieldDisabled}
            onFocus={() => {
              if (isFeeField(field.key)) {
                setIsEditingFee(true);
              }
            }}
            onBlur={(e) => {
              if (isFeeField(field.key)) {
                handleFeeFieldBlur(e, name);
              }
            }}
            onChange={(e) => {
              if (!isFieldDisabled) {
                if (isFeeField(field.key)) {
                  handleFeeFieldChange(e, name);
                } else {
                  enhancedSetFieldValue(name, e.target.value);
                }
              }
            }}
          />
          {hasError && <FormFeedback>{errorMessage}</FormFeedback>}
          {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
          {field.description && !isFieldDisabled && !isTenantField && <FormText>{field.description}</FormText>}
        </FormGroup>
      );

    case "number":
      const numberValue = getNestedValue(values, name);
      const formatNumberValue = (value, isEditing = false) => {
        if (value === null || value === undefined || value === '') {
          return '';
        }
        if (isEditing) {
          const num = parseFloat(value);
          return isNaN(num) ? '' : num.toString();
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
          return '';
        }
        const keyLower = field.key.toLowerCase();
        if (keyLower.includes('fee') || keyLower.includes('sd') || keyLower.includes('targetrent') || keyLower.includes('basicrent')) {
          return `$${num.toFixed(2)}`;
        }
        return num.toString();
      };

      const handleNumberChange = (e, fieldName) => {
        const rawValue = e.target.value.replace(/[^\d.]/g, '');
        const decimalCount = (rawValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
          return;
        }
        setFieldValue(fieldName, rawValue);
      };

      const handleNumberBlur = (e, fieldName) => {
        setIsEditingNumber(false);
        const cleanValue = e.target.value.replace(/[^\d.]/g, '');
        if (cleanValue === '') {
          setFieldValue(fieldName, '');
        } else {
          const numValue = parseFloat(cleanValue);
          if (!isNaN(numValue)) {
            setFieldValue(fieldName, numValue);
          } else {
            setFieldValue(fieldName, '');
          }
        }
      };

      return (
        <FormGroup className={isNested ? "mb-2" : "mb-3"}>
          <Label for={name}>{field.label}{isRequired && <span className="text-danger">*</span>}</Label>
          <Input
            name={name}
            id={name}
            type="text"
            placeholder={field.example !== undefined ? (
              field.key.toLowerCase().includes('fee') ||
                field.key.toLowerCase().includes('sd') ||
                field.key.toLowerCase().includes('targetrent') ||
                field.key.toLowerCase().includes('basicrent')
                ? `$${field.example.toFixed(2)}`
                : field.example.toString()
            ) : ""}
            min={field.min}
            max={field.max}
            step={field.step || "any"}
            value={formatNumberValue(numberValue, isEditingNumber)}
            invalid={!!hasError}
            disabled={isFieldDisabled}
            onFocus={() => setIsEditingNumber(true)}
            onChange={(e) => {
              if (!isFieldDisabled) {
                handleNumberChange(e, name);
              }
            }}
            onBlur={(e) => {
              if (!isFieldDisabled) {
                handleNumberBlur(e, name);
              }
            }}
            onKeyDown={(e) => {
              const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'];
              if (!allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
          {hasError && <FormFeedback>{errorMessage}</FormFeedback>}
          {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
          {field.description && !isFieldDisabled && <FormText>{field.description}</FormText>}
        </FormGroup>
      );

    case "date":
      return (
        <FormGroup className={isNested ? "mb-2" : "mb-3"}>
          <Label for={name}>{field.label}{isRequired && <span className="text-danger">*</span>}</Label>
          <Input
            id={name}
            type="date"
            name={name}
            value={safeFormatDate(getNestedValue(values, name))}
            disabled={isFieldDisabled}
            onChange={(e) => {
              if (!isFieldDisabled) {
                setFieldValue(name, e.target.value ? new Date(e.target.value) : null);
              }
            }}
            invalid={!!hasError}
          />
          {hasError && <FormFeedback>{errorMessage}</FormFeedback>}
          {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
          {field.description && !isFieldDisabled && <FormText>{field.description}</FormText>}
        </FormGroup>
      );

    case "select": {
      const hasEnumValues = field.enum && field.enum.length > 0;
      const optionsArray = hasEnumValues ? field.enum : field.options || [];
      const options = optionsArray.map(option => ({
        value: String(option),
        label: String(option)
      }));
      const currentValue = getNestedValue(values, name);
      const selectedOption = currentValue
        ? typeof currentValue === 'object'
          ? { ...currentValue, value: String(currentValue.value), label: String(currentValue.label) }
          : options.find(opt => String(opt.value) === String(currentValue)) || { value: String(currentValue), label: String(currentValue) }
        : null;

      const selectProps = {
        id: name,
        name,
        value: selectedOption,
        options,
        onChange: (option) => {
          if (!isFieldDisabled) {
            const convertedOption = option ? {
              ...option,
              value: autoConvertValue(option.value, field.type)
            } : null;
            setFieldValue(name, convertedOption);
          }
        },
        placeholder: `Select ${field.label}`,
        isClearable: !isRequired,
        isDisabled: isFieldDisabled,
        className: hasError ? 'is-invalid' : '',
        styles: {
          control: (base) => ({
            ...base,
            borderColor: hasError ? '#dc3545' : base.borderColor,
            '&:hover': {
              borderColor: hasError ? '#dc3545' : base.borderColor
            }
          })
        }
      };

      return (
        <FormGroup className={isNested ? "mb-2" : "mb-3"}>
          <Label for={name}>
            {field.label}{isRequired && <span className="text-danger">*</span>}
          </Label>
          {hasEnumValues ? (
            <Select {...selectProps} />
          ) : (
            <CreatableSelect
              {...selectProps}
              formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
              onCreateOption={(inputValue) => {
                if (!isFieldDisabled) {
                  const newOption = { value: String(inputValue), label: String(inputValue) };
                  setFieldValue(name, newOption);
                }
              }}
            />
          )}
          {hasError && <div className="invalid-feedback d-block">{errorMessage}</div>}
          {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
          {field.description && !isFieldDisabled && <FormText>{field.description}</FormText>}
        </FormGroup>
      );
    }

    case "boolean":
      return (
        <FormGroup check className={isNested ? "mb-2" : "mb-3"}>
          <Label check>
            <Field
              name={name}
              type="checkbox"
              checked={getNestedValue(values, name) || false}
              disabled={isFieldDisabled}
              onChange={(e) => {
                if (!isFieldDisabled) {
                  setFieldValue(name, e.target.checked);
                }
              }}
              as={Input}
            />
            {field.label}
          </Label>
          {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
          {field.description && !isFieldDisabled && <FormText>{field.description}</FormText>}
        </FormGroup>
      );

    case "group":
      return (
        <Card className={isNested ? "mb-2" : "mb-3"}>
          <CardHeader className="py-3 text-center">
            <Button
              color="primary"
              outline
              className="fw-bold px-4 py-2 rounded-pill"
              onClick={() => toggleGroup(name)}
            >
              <FeatherIcon icon="folder" className="me-2" size={16} />
              {field.label}
            </Button>
          </CardHeader>
          <Collapse isOpen={openGroups[name] !== false}>
            <CardBody>
              {field.description && (
                <p className="text-muted small mb-3">{field.description}</p>
              )}
              <Row>
                {field.fields.map((subField, index) => (
                  <Col md={6} key={index}>
                    <DynamicField
                      field={subField}
                      name={`${name}.${subField.key}`}
                      values={values}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      touched={touched}
                      isNested={true}
                      isCompanyUser={isCompanyUser}
                    />
                  </Col>
                ))}
              </Row>
            </CardBody>
          </Collapse>
        </Card>
      );

    case "array":
      return (
        <Card className={isNested ? "mb-2" : "mb-3"}>
          <CardHeader className="py-3 text-center">
            <Button
              color="primary"
              outline
              className="fw-bold px-4 py-2 rounded-pill"
              onClick={() => toggleGroup(name)}
            >
              <FeatherIcon icon="list" className="me-2" size={16} />
              {field.label}
              {isTenantField && (
                <span className="ms-2 text-muted">
                  (Residents: {residentInfo.count})
                </span>
              )}
            </Button>
          </CardHeader>
          <Collapse isOpen={openGroups[name] !== false}>
            <CardBody>
              {field.description && (
                <p className="text-muted small mb-3">{field.description}</p>
              )}
              <ArrayFieldComponent
                arrayConfig={field}
                values={values}
                setFieldValue={setFieldValue}
                arrayName={name}
                errors={errors}
                touched={touched}
                isCompanyUser={isCompanyUser}
              />
              {isTenantField && (
                <FormText>{residentInfo.explanation}</FormText>
              )}
            </CardBody>
          </Collapse>
        </Card>
      );

    default:
      return (
        <FormGroup className={isNested ? "mb-2" : "mb-3"}>
          <Label for={name}>{field.label}{field.required && <span className="text-danger">*</span>}</Label>
          <Field
            name={name}
            id={name}
            as={Input}
            value={getDisplayValue(getNestedValue(values, name), field)}
            invalid={!!hasError}
            disabled={isFieldDisabled}
            onChange={(e) => {
              if (!isFieldDisabled) {
                enhancedSetFieldValue(name, e.target.value);
              }
            }}
          />
          {hasError && <FormFeedback>{errorMessage}</FormFeedback>}
          {isFieldDisabled && <FormText>This field cannot be edited.</FormText>}
          {field.description && !isFieldDisabled && <FormText>{field.description}</FormText>}
        </FormGroup>
      );
  }
};

const ArrayFieldComponent = ({ arrayConfig, values, setFieldValue, arrayName, errors, touched, isCompanyUser }) => {
  const { openGroups, toggleGroup } = React.useContext(AccordionContext);

  return (
    <FieldArray name={arrayName}>
      {({ push, remove }) => (
        <>
          {values[arrayName]?.map((item, index) => (
            <Card key={index} className="mb-3">
              <CardHeader className="py-3 text-center">
                <Button
                  color="primary"
                  outline
                  className="fw-bold px-4 py-2 rounded-pill"
                  onClick={() => toggleGroup(`${arrayName}[${index}]`)}
                >
                  <FeatherIcon icon="list" className="me-2" size={16} />
                  {arrayConfig.label} #{index + 1}
                </Button>
                {values[arrayName].length > (arrayConfig.minItems || 1) && (
                  <Button
                    color="danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => remove(index)}
                  >
                    Remove
                  </Button>
                )}
              </CardHeader>
              <Collapse isOpen={openGroups[`${arrayName}[${index}]`] !== false}>
                <CardBody>
                  <Row>
                    {arrayConfig.fields.map((field, fieldIndex) => (
                      <Col md={field.type === 'group' ? 12 : 6} key={fieldIndex}>
                        <DynamicField
                          field={field}
                          name={`${arrayName}[${index}].${field.key}`}
                          values={values}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          touched={touched}
                          isCompanyUser={isCompanyUser}
                        />
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Collapse>
            </Card>
          ))}
          {(!arrayConfig.maxItems || values[arrayName].length < arrayConfig.maxItems) && (
            <div className="text-center mb-4">
              <Button
                color="secondary"
                outline
                onClick={() => {
                  const newItem = {};
                  arrayConfig.fields.forEach(field => {
                    let defaultValue = field.defaultValue !== undefined ? field.defaultValue :
                      field.type === 'boolean' ? false :
                        field.type === 'array' ? [] :
                          field.type === 'group' ? {} : "";
                    defaultValue = autoConvertValue(defaultValue, field.type);
                    newItem[field.key] = defaultValue;
                  });
                  push(newItem);
                  const newIndex = values[arrayName].length;
                  toggleGroup(`${arrayName}[${newIndex}]`, true);
                }}
              >
                Add {arrayConfig.label}
              </Button>
            </div>
          )}
        </>
      )}
    </FieldArray>
  );
};

const generateInitialValues = (formLayout) => {
  const initialValues = {};

  const processField = (field, apiValue = undefined) => {
    if (field.type === "array") {
      const arrayItem = {};
      if (field.fields) {
        field.fields.forEach(subField => {
          arrayItem[subField.key] = processField(subField);
        });
      }
      const initialItems = [];
      const minItems = field.minItems || 1;
      for (let i = 0; i < minItems; i++) {
        initialItems.push({ ...arrayItem });
      }
      return initialItems;
    } else if (field.type === "group") {
      const groupValue = {};
      if (field.fields) {
        field.fields.forEach(subField => {
          groupValue[subField.key] = processField(subField);
        });
      }
      return groupValue;
    } else if (field.type === "date") {
      if (!apiValue) return null;
      try {
        const date = new Date(apiValue);
        return !isNaN(date.getTime()) ? date : null;
      } catch (error) {
        console.error("Error converting date:", error);
        return null;
      }
    } else {
      let value = apiValue !== undefined ? apiValue :
        field.defaultValue !== undefined ? field.defaultValue :
          field.type === "boolean" ? false :
            field.type === "number" ? "" :
              field.type === "select" ? null : "";
      return autoConvertValue(value, field.type);
    }
  };

  initialValues['meta'] = {
    accounts: { heat: "", water: "", electricity: "" },
    parking: [],
    storage: [],
    fobs: [],
    keys: [],
    occupants: []
  };
  initialValues['documentsNA'] = DOCUMENT_TYPES
    .filter(doc => doc.alwaysVisible)
    .reduce((acc, docType) => ({
      ...acc,
      [docType.id]: false,
    }), {});
  formLayout.forEach(field => {
    initialValues[field.key] = processField(field);
  });

  return initialValues;
};

const convertApiToFormValues = (apiData, formLayout) => {
  if (!apiData) return null;

  const formValues = {};

  const parseDate = (value) => {
    if (!value) return null;
    const isoDate = new Date(value);
    if (!isNaN(isoDate.getTime())) return isoDate;
    const dmyMatch = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
    if (dmyMatch) {
      const [, day, month, year] = dmyMatch;
      const parsed = new Date(`${year}-${month}-${day}`);
      return !isNaN(parsed.getTime()) ? parsed : null;
    }
    return null;
  };

  const processField = (field, apiValue) => {
    if (apiValue === undefined || apiValue === null) return null;

    if (field.type === "array") {
      return Array.isArray(apiValue)
        ? apiValue.map(item => {
          const arrayItemValues = {};
          if (field.fields) {
            field.fields.forEach(subField => {
              let processedValue = processField(subField, item[subField.key]);
              arrayItemValues[subField.key] = autoConvertValue(processedValue, subField.type);
            });
          }
          return arrayItemValues;
        })
        : [];
    }

    if (field.type === "group") {
      const groupValues = {};
      if (field.fields) {
        field.fields.forEach(subField => {
          let processedValue = processField(subField, apiValue[subField.key]);
          groupValues[subField.key] = autoConvertValue(processedValue, subField.type);
        });
      }
      return groupValues;
    }

    if (field.type === "select") {
      if (apiValue && typeof apiValue === 'string') {
        return { value: String(apiValue), label: String(apiValue) };
      } else if (typeof apiValue === 'number') {
        return { value: String(apiValue), label: String(apiValue) };
      }
      return apiValue;
    }

    if (field.key === 'status' && field.type === 'text') {
      return String(apiValue);
    }

    if (field.type === "date") {
      return parseDate(apiValue);
    }

    return autoConvertValue(apiValue, field.type);
  };

  formValues['meta'] = {
    accounts: {
      heat: apiData.meta?.accounts?.heat || "",
      water: apiData.meta?.accounts?.water || "",
      electricity: apiData.meta?.accounts?.electricity || "",
    },
    parking: Array.isArray(apiData.meta?.parking) ? apiData.meta.parking : [],
    storage: Array.isArray(apiData.meta?.storage) ? apiData.meta.storage : [],
    fobs: Array.isArray(apiData.meta?.fobs) ? apiData.meta.fobs : [],
    keys: Array.isArray(apiData.meta?.keys) ? apiData.meta.keys : [],
    occupants: Array.isArray(apiData.meta?.occupants) ? apiData.meta.occupants : []
  };

  formValues['documentsNA'] = apiData.documentsNA || DOCUMENT_TYPES.reduce((acc, docType) => ({
    ...acc,
    [docType.id]: false,
  }), {});

  formLayout.forEach(field => {
    formValues[field.key] = processField(field, apiData[field.key]);
  });

  return formValues;
};

const DynamicForm = ({
  show,
  handleClose,
  propertyId,
  propertyName = "",
  buildingName = null,
  onSubmit,
  editData = null,
  editMode = false,
  isCompanyUser,
  tenantInfo,
  utilitiesAvailable = null
}) => {
  const buildTenantInfoString = (tenantData = {}) => {
    const unitNo = tenantData?.unitNo || 'N/A';
    const address = buildingName || 'N/A';
    const tenantName = tenantData?.tenants || tenantData?.tenant || 'N/A';
    const fullAddress = `${propertyName !== 'N/A' ? propertyName + ' ' : ''}${address}`;

    return (
      <div className="text-center">
        <h5 className="mb-3 fw-bold text-primary">{tenantName}</h5>
        <div className="d-flex justify-content-center align-items-center text-muted fs-6">
          <FeatherIcon icon="home" className="me-2 text-primary" size={18} />
          <span>Unit No. {unitNo}</span>
          <FeatherIcon icon="map-pin" className="ms-3 me-2 text-primary" size={18} />
          <span>Address: {fullAddress}</span>
        </div>
      </div>
    );
  };

  const [formLayout, setFormLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationSchema, setValidationSchema] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [documentErrors, setDocumentErrors] = useState(
    DOCUMENT_TYPES.reduce((acc, docType) => ({
      ...acc,
      [docType.id]: '',
    }), {})
  );

  const toggleGroup = (groupId, forceOpen) => {
    setOpenGroups(prev => ({
      ...prev,
      [groupId]: forceOpen !== undefined ? forceOpen : !prev[groupId]
    }));
  };

  useEffect(() => {
    const fetchLayout = async () => {
      try {
        setLoading(true);
        const layoutData = await getMasterSheetLayout(propertyId, buildingName);

        if (layoutData && layoutData.fields) {
          setFormLayout(layoutData.fields);
          const schema = generateValidationSchema(layoutData.fields, utilitiesAvailable);
          setValidationSchema(schema);

          const initialAccordionState = {};
          initialAccordionState['basicFields'] = false;
          initialAccordionState['meta'] = false;
          initialAccordionState['general'] = false;

          const initializeAccordions = (fields, parentPath = '') => {
            fields.forEach(field => {
              if (field.type === 'group') {
                const fieldPath = parentPath ? `${parentPath}.${field.key}` : field.key;
                initialAccordionState[fieldPath] = true;
                if (field.fields) {
                  initializeAccordions(field.fields, fieldPath);
                }
              } else if (field.type === 'array' && field.fields) {
                field.fields.forEach(arrayField => {
                  if (arrayField.type === 'group' && arrayField.fields) {
                    initializeAccordions([arrayField], `${field.key}[0]`);
                  }
                });
              }
            });
          };

          initializeAccordions(layoutData.fields);
          setOpenGroups(initialAccordionState);

          if (editMode && editData) {
            const convertedValues = convertApiToFormValues(editData, layoutData.fields);
            setInitialFormValues(convertedValues);
          } else {
            const defaultValues = generateInitialValues(layoutData.fields);
            setInitialFormValues(defaultValues);
          }
        } else {
          setError("Failed to load form layout");
        }
      } catch (err) {
        setError("Error loading form layout: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (show && propertyId) {
      fetchLayout();
    }
  }, [propertyId, buildingName, show, editData, editMode, isCompanyUser, utilitiesAvailable]);

  const prepareFormData = (values) => {
    const processValue = (value, fieldSchema) => {
      if (value === null || value === undefined) {
        return value;
      }

      if (fieldSchema?.key === 'status' && fieldSchema?.type === 'text') {
        return String(value);
      }

      if (typeof value === 'object' && value !== null && 'value' in value && 'label' in value) {
        return autoConvertValue(value.value, fieldSchema?.type);
      }

      if (Array.isArray(value)) {
        return value.map(item => {
          if (typeof item === 'object' && item !== null) {
            const processedItem = {};
            if (fieldSchema?.fields) {
              fieldSchema.fields.forEach(subField => {
                processedItem[subField.key] = processValue(item[subField.key], subField);
              });
            } else {
              Object.keys(item).forEach(key => {
                processedItem[key] = processValue(item[key], null);
              });
            }
            return processedItem;
          }
          return autoConvertValue(item, fieldSchema?.type);
        });
      }

      if (typeof value === 'object' && !(value instanceof Date)) {
        const processedObj = {};
        if (fieldSchema?.fields) {
          fieldSchema.fields.forEach(subField => {
            processedObj[subField.key] = processValue(value[subField.key], subField);
          });
        } else {
          Object.keys(value).forEach(key => {
            processedObj[key] = processValue(value[key], null);
          });
        }
        return processedObj;
      }

      if (value instanceof Date) {
        try {
          return !isNaN(value.getTime()) ? value.toISOString() : null;
        } catch (error) {
          console.error("Error converting date:", error);
          return null;
        }
      }

      if (fieldSchema?.key?.toLowerCase().includes('fee') && typeof value === 'string') {
        return value.replace(/^\$/, '');
      }

      return autoConvertValue(value, fieldSchema?.type);
    };

    const processedValues = {};
    formLayout.forEach(field => {
      processedValues[field.key] = processValue(values[field.key], field);
    });

    const meta = values.meta;
    const hasMetaChanges =
      (meta.accounts && (
        meta.accounts.heat ||
        meta.accounts.water ||
        meta.accounts.electricity
      )) ||
      (meta.parking && meta.parking.length > 0) ||
      (meta.storage && meta.storage.length > 0) ||
      (meta.fobs && meta.fobs.length > 0) ||
      (meta.keys && meta.keys.length > 0) ||
      (meta.occupants && meta.occupants.length > 0);

    if (hasMetaChanges) {
      processedValues['meta'] = {
        accounts: {
          heat: meta.accounts.heat || "",
          water: meta.accounts.water || "",
          electricity: meta.accounts.electricity || "",
        },
        parking: meta.parking,
        storage: meta.storage,
        fobs: meta.fobs,
        keys: meta.keys,
        occupants: meta.occupants
      };
    }

    processedValues['documentsNA'] = values.documentsNA;

    if (editMode && editData?._id) {
      processedValues._id = editData._id;
    }

    return processedValues;
  };

  const getFieldLabelByPath = (path, fields = formLayout) => {
    const labelMap = {
      'meta.accounts.heat': 'Heat Provider/Account Number',
      'meta.accounts.water': 'Water Provider/Account Number',
      'meta.accounts.electricity': 'Electricity Provider/Account Number',
      'insuranceExpiry': 'Insurance Expiry',
      'meta.parking': 'Parking Entry',
      'meta.storage': 'Storage Entry',
      'meta.fobs': 'FOB Entry',
      'meta.keys': 'Key Set',
      'meta.occupants': 'Occupant'
    };

    // Handle array fields like meta.parking[0].type
    const arrayMatch = path.match(/^(\w+)\[\d+\]\.(.+)$/);
    if (arrayMatch) {
      const arrayName = arrayMatch[1];
      const subPath = arrayMatch[2];
      const baseLabel = labelMap[arrayName] || arrayName;
      const field = fields.find(f => f.key === arrayName);
      if (field && field.fields) {
        const subField = field.fields.find(f => f.key === subPath);
        return subField ? `${baseLabel} - ${subField.label || subPath}` : baseLabel;
      }
    }

    for (const field of fields) {
      if (field.key === path) {
        return field.label || path;
      }
      if (field.type === 'group' && field.fields) {
        const nestedLabel = getFieldLabelByPath(path, field.fields);
        if (nestedLabel !== path) return nestedLabel;
      }
      if (field.type === 'array' && field.fields) {
        const arraySubMatch = path.match(new RegExp(`^${field.key}\\[\\d+\\]\\.(.+)$`));
        if (arraySubMatch) {
          const nestedPath = arraySubMatch[1];
          const nestedLabel = getFieldLabelByPath(nestedPath, field.fields);
          if (nestedLabel !== nestedPath) {
            return `${field.label || field.key} - ${nestedLabel}`;
          }
        }
      }
    }
    return labelMap[path] || path;
  };

  const validate = (values) => {
    if (!validationSchema) return {};

    try {
      validationSchema.validateSync(values, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};

      if (err.inner) {
        err.inner.forEach((e) => {
          if (!errors[e.path]) {
            errors[e.path] = e.message;
          }
        });
      }

      const documentErrorMessages = Object.values(documentErrors).filter(error => error);
      if (documentErrorMessages.length > 0) {
        errors.documents = 'Please resolve all document errors.';
      }

      return errors;
    }
  };

  const validateWithAlert = (values) => {
    if (!validationSchema) return {};

    try {
      validationSchema.validateSync(values, { abortEarly: false });
      return {};
    } catch (err) {
      const errors = {};
      const allErrors = [];
      const shownMessages = new Set();

      if (err.inner) {
        err.inner.forEach((e) => {
          if (!errors[e.path]) {
            errors[e.path] = e.message;
            if (!shownMessages.has(e.message)) {
              shownMessages.add(e.message);
              const fieldLabel = getFieldLabelByPath(e.path);
              allErrors.push(`${fieldLabel}: ${e.message}`);
            }
          }
        });
      }

      const documentErrorMessages = Object.values(documentErrors).filter(error => error);
      if (documentErrorMessages.length > 0) {
        errors.documents = 'Please resolve all document errors.';
        allErrors.push(...documentErrorMessages);
      }

      if (allErrors.length > 0) {
        Swal.fire({
          title: 'Form Validation Errors',
          html: `
            <div style="text-align: left; max-height: 400px; overflow-y: auto; font-size: 0.95rem;">
              <p style="margin-bottom: 15px;">Please correct the following issues:</p>
              <ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
                ${allErrors.map(err => `<li style="margin-bottom: 10px; line-height: 1.4;">${err}</li>`).join('')}
              </ul>
            </div>
          `,
          icon: 'error',
          confirmButtonText: 'Review Form',
          width: '650px',
          padding: '1.5rem',
          customClass: {
            title: 'fs-5 fw-bold',
            htmlContainer: 'text-start',
            confirmButton: 'btn btn-primary px-4'
          },
          buttonsStyling: false
        });
      }

      return errors;
    }
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) {
      validateWithAlert(values);
      setSubmitting(false);
      return;
    }

    Swal.fire({
      title: editMode ? 'Confirm Update' : 'Confirm Submission',
      html: `
        <div style="text-align: left; font-size: 0.95rem;">
          <p>Are you sure you want to ${editMode ? 'update' : 'submit'} this tenant information?</p>
          <p className="text-muted small">Please review all fields before confirming.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: editMode ? 'Update' : 'Submit',
      cancelButtonText: 'Continue Editing',
      reverseButtons: true,
      width: '600px',
      customClass: {
        title: 'fs-5 fw-bold',
        confirmButton: 'btn btn-primary px-4',
        cancelButton: 'btn btn-secondary px-4 me-2'
      },
      buttonsStyling: false
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Processing...',
          html: '<p style="font-size: 0.95rem;">Please wait while we process your request.</p>',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          },
          customClass: {
            title: 'fs-5 fw-bold'
          }
        });

        const processedValues = prepareFormData(values);

        Promise.resolve(onSubmit(processedValues, editMode))
          .then(() => {
            Swal.close();
            Swal.fire({
              title: 'Success',
              html: `
                <div style="text-align: left; font-size: 0.95rem;">
                  <p>Tenant information ${editMode ? 'updated' : 'submitted'} successfully.</p>
                </div>
              `,
              icon: 'success',
              confirmButtonText: 'Close',
              width: '600px',
              customClass: {
                title: 'fs-5 fw-bold',
                confirmButton: 'btn btn-primary px-4'
              },
              buttonsStyling: false
            }).then(() => {
              handleClose();
            });
          })
          .catch((error) => {
            Swal.close();
            Swal.fire({
              title: 'Error',
              html: `
                <div style="text-align: left; font-size: 0.95rem;">
                  <p>Failed to ${editMode ? 'update' : 'submit'} tenant information:</p>
                  <p class="text-muted">${error.message || error}</p>
                </div>
              `,
              icon: 'error',
              confirmButtonText: 'Try Again',
              width: '600px',
              customClass: {
                title: 'fs-5 fw-bold',
                confirmButton: 'btn btn-primary px-4'
              },
              buttonsStyling: false
            });
          })
          .finally(() => {
            setSubmitting(false);
          });
      } else {
        setSubmitting(false);
      }
    });
  };

  if (loading) {
    return (
      <Modal isOpen={show} toggle={handleClose} size="xl">
        <ModalHeader toggle={handleClose} className="justify-content-center">
          Loading Form...
        </ModalHeader>
        <ModalBody className="text-center p-5">
          <Spinner />
          <p className="mt-3">Loading form data...</p>
        </ModalBody>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal isOpen={show} toggle={handleClose} size="xl">
        <ModalHeader toggle={handleClose} className="justify-content-center">
          Error
        </ModalHeader>
        <ModalBody>
          <Alert color="danger" className="text-center">
            {error}
          </Alert>
        </ModalBody>
      </Modal>
    );
  }

  if (!formLayout || !initialFormValues) {
    return (
      <Modal isOpen={show} toggle={handleClose} size="xl">
        <ModalHeader toggle={handleClose} className="justify-content-center">
          No Form Layout Found
        </ModalHeader>
        <ModalBody>
          <Alert color="warning" className="text-center">
            No form layout was found for this property.
          </Alert>
        </ModalBody>
      </Modal>
    );
  }

  const hasBasicFields = formLayout.some(field => field.type !== "array" && field.type !== "group" && field.key !== "status" && field.key !== "comments");

  return (
    <Modal
      isOpen={show}
      toggle={handleClose}
      backdrop="static" 
      keyboard={false}
      style={{ maxWidth: '85vw', width: '85vw' }}
      className="modal-xxl"
    >
      <ModalHeader toggle={handleClose} className="justify-content-center">
        {buildTenantInfoString(tenantInfo)}
      </ModalHeader>
      <ModalBody>
        <div className="container-fluid">
          <AccordionContext.Provider value={{ openGroups, toggleGroup }}>
            <Formik
              initialValues={initialFormValues}
              validationSchema={validationSchema}
              enableReinitialize={true}
              validate={validate}
              onSubmit={handleFormSubmit}
            >
              {({ values, setFieldValue, errors, touched, isSubmitting, validateForm, handleSubmit, submitForm }) => (
                <Form onSubmit={handleSubmit}>
                  {formLayout
                    .filter(field => field.type === "array" || field.type === "group")
                    .map((field, index) => {
                      if (field.type === "array") {
                        return (
                          <Col md={12} key={index} className="mb-4 px-0">
                            <ArrayFieldComponent
                              arrayConfig={field}
                              values={values}
                              setFieldValue={setFieldValue}
                              arrayName={field.key}
                              errors={errors}
                              touched={touched}
                              isCompanyUser={isCompanyUser}
                            />
                          </Col>
                        );
                      } else if (field.type === "group") {
                        return (
                          <Col md={12} key={index} className="mb-3 px-0">
                            <DynamicField
                              field={field}
                              name={field.key}
                              values={values}
                              setFieldValue={setFieldValue}
                              errors={errors}
                              touched={touched}
                              isCompanyUser={isCompanyUser}
                            />
                          </Col>
                        );
                      }
                      return null;
                    })}

                  {hasBasicFields && (
                    <Card className="mb-4">
                      <CardHeader className="py-3 text-center">
                        <Button
                          color={hasBasicFieldErrors(errors, touched, formLayout) ? "primary" : "danger"}
                          outline
                          className="fw-bold px-4 py-2 rounded-pill"
                          onClick={() => toggleGroup('basicFields')}
                        >
                          <FeatherIcon icon="user" className="me-2" size={16} />
                          Tenant Basic Information
                        </Button>
                      </CardHeader>
                      <Collapse isOpen={openGroups['basicFields'] !== false}>
                        <CardBody>
                          <Row>
                            {formLayout
                              .filter(field => field.type !== "array" && field.type !== "group" && field.key !== "status" && field.key !== "comments")
                              .map((field, index) => {
                                const colSize = 3;
                                return (
                                  <Col md={colSize} key={index}>
                                    <DynamicField
                                      field={field}
                                      name={field.key}
                                      values={values}
                                      setFieldValue={setFieldValue}
                                      errors={errors}
                                      touched={touched}
                                      isCompanyUser={isCompanyUser}
                                    />
                                  </Col>
                                );
                              })}
                          </Row>
                        </CardBody>
                      </Collapse>
                    </Card>
                  )}

                  <MetaFields
                    values={values}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    toggleGroup={toggleGroup}
                    openGroups={openGroups}
                    utilitiesAvailable={utilitiesAvailable}
                  />

                  <Leases propertyId={propertyId} buildingName={buildingName} unitNo={initialFormValues?.unitNo || ''} />
                  <Addendums
                    propertyId={propertyId}
                    buildingName={buildingName}
                    propertyName={propertyName}
                    unitNo={initialFormValues?.unitNo || ''}
                    itemId={editData?._id}
                    item={editData}
                    tenants={values?.tenants}
                    fields={formLayout}
                  />
                  <Documents
                    propertyId={propertyId}
                    buildingName={buildingName}
                    unitNo={initialFormValues?.unitNo || ''}
                    setFieldValue={setFieldValue}
                    documentErrors={documentErrors}
                    setDocumentErrors={setDocumentErrors}
                    documentsNA={values.documentsNA}
                    setDocumentsNA={(newNA) => setFieldValue('documentsNA', newNA)}
                  />
                  <Pictures propertyId={propertyId} buildingName={buildingName} unitNo={initialFormValues?.unitNo || ''} />
                  <Memos
                    itemId={editData?._id || ''}
                    createdAt={editData?.createdAt}
                    updatedAt={editData?.updatedAt}
                  />

                  <div className="mt-4 d-flex justify-content-end">
                    <Button
                      color="secondary"
                      onClick={handleClose}
                      className="me-2 px-4"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      type="button"
                      disabled={isSubmitting}
                      className="px-4"
                      onClick={async () => {
                        const formErrors = await validateForm();
                        if (Object.keys(formErrors).length > 0) {
                          validateWithAlert(values);
                          submitForm();
                        } else {
                          submitForm();
                        }
                      }}
                    >
                      {isSubmitting ? <Spinner size="sm" className="me-2" /> : null}
                      {editMode ? "Update" : "Submit"}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </AccordionContext.Provider>
        </div>
      </ModalBody>
    </Modal>
  );
};

const TenantForm = ({
  show,
  handleClose,
  propertyId,
  propertyName,
  buildingName,
  onSubmit,
  editData = null,
  editMode = false,
  isCompanyUser,
  tenantInfo,
  utilitiesAvailable = null
}) => {
  return (
    <DynamicForm
      show={show}
      handleClose={handleClose}
      propertyId={propertyId}
      propertyName={propertyName}
      buildingName={buildingName}
      onSubmit={onSubmit}
      editData={editData}
      editMode={editMode}
      isCompanyUser={isCompanyUser}
      tenantInfo={tenantInfo}
      utilitiesAvailable={utilitiesAvailable}
    />
  );
};

export { TenantForm };